import { FunctionComponent, PropsWithChildren } from 'react';

import { JSX } from '@emotion/react/jsx-runtime';
import { useField } from 'react-final-form';

import {
  ConditionalWrapper,
  Tooltip,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { WoopAgentCommand } from '../../../_components/WoopAgentCommand';
import { useVersionsInfo } from '../../../_hooks/useVersionsInfo';
import { vpaSettingsConstants } from '../../_constants/settingsConstants';
import { SettingsInfoCallout } from '../SettingsInfoCallout';

import {
  DurationPresetInput,
  EVICTION_DELAY_PRESETS,
  RffCheckbox,
  RffControlsProvider,
  useControlsGroupState,
} from '@cast/shared-rff-ui';

import { getDocsLink } from '@cast/utils';

type RolloutBehaviourOptionProps = {
  name: string;
  testId?: string;
  checkboxTestId: string;
  title: string;
  description: string;
  isDisabled: boolean;
  size: 'small' | 'medium';
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  content?: JSX.Element;
};

const RolloutBehaviourOption = ({
  name,
  testId,
  checkboxTestId,
  title,
  description,
  isDisabled,
  size,
  LabelComponent,
  content,
}: RolloutBehaviourOptionProps) => {
  const { latestVersion } = useVersionsInfo();
  const labelTextVariant = size === 'small' ? 'P10M' : 'P12M';

  return (
    <Stack rowGap={8} {...(testId ? { 'data-testid': testId } : {})}>
      <ConditionalWrapper
        condition={isDisabled}
        wrapper={(child) => (
          <Tooltip
            title={
              <Stack
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <span>
                  Update required:&nbsp;
                  <ExternalLink
                    link={getDocsLink('docs_woop_upgrading')}
                    noIcon
                  >
                    <Typography
                      variant="P12B"
                      sx={{ color: tokens.theme.fg.selection.primary }}
                    >
                      workload-autoscaler {latestVersion}
                    </Typography>
                  </ExternalLink>
                </span>
                <WoopAgentCommand />
              </Stack>
            }
          >
            {child}
          </Tooltip>
        )}
      >
        <Stack direction="row" gap={4} alignItems="center">
          <RffCheckbox
            name={name}
            size="small"
            testId={checkboxTestId}
            sx={{ mr: 4 }}
            disabled={isDisabled || undefined}
          />
          <ConditionalWrapper
            condition={!!LabelComponent}
            wrapper={(c) =>
              LabelComponent ? <LabelComponent>{c}</LabelComponent> : c
            }
          >
            <Typography variant={labelTextVariant}>{title}</Typography>
          </ConditionalWrapper>
        </Stack>
      </ConditionalWrapper>
      <Stack rowGap={8} px={24}>
        <Typography
          variant="P10R"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          {description}
        </Typography>

        {content}
      </Stack>
    </Stack>
  );
};

const DelaySecondsField = ({
  size,
  LabelComponent,
}: {
  size: 'small' | 'medium';
  LabelComponent?: FunctionComponent<PropsWithChildren>;
}) => {
  const { name: enabledFieldName = '' } = useControlsGroupState({
    name: 'delaySecondsEnabled',
  });
  const {
    input: { value: isEnabled },
  } = useField(enabledFieldName);

  const { delaySeconds } = vpaSettingsConstants.autoscaler.rolloutBehaviour;

  return (
    <RolloutBehaviourOption
      testId="delay-seconds-field"
      name="delaySecondsEnabled"
      checkboxTestId="delay-seconds-enabled-checkbox"
      title={delaySeconds.title}
      description={delaySeconds.description}
      isDisabled={false}
      size={size}
      LabelComponent={LabelComponent}
      content={
        isEnabled && (
          <RffControlsProvider namePrefix="delaySeconds">
            <DurationPresetInput
              units={['minutes', 'seconds']}
              presets={EVICTION_DELAY_PRESETS}
              LabelComponent={LabelComponent}
              testId="delay-seconds-input"
            />
          </RffControlsProvider>
        )
      }
    />
  );
};

export const RolloutBehaviourField = ({
  size = 'medium',
  layout = 'vertical',
  LabelComponent,
}: {
  size?: 'small' | 'medium';
  layout?: 'horizontal' | 'vertical';
  LabelComponent?: FunctionComponent<PropsWithChildren>;
}) => {
  const { isRestartPodsOneByOneCannotBeUsed, isZeroDowntimeCannotBeUsed } =
    useVersionsInfo();

  const { evictPodsOneByOne, zeroDowntime } =
    vpaSettingsConstants.autoscaler.rolloutBehaviour;

  const zeroDowntimeInfo = (
    <>
      Requires workloads that support multiple replicas. See the{' '}
      <ExternalLink link={getDocsLink('docs_woop_single_replica')} noIcon>
        documentation
      </ExternalLink>{' '}
      for all prerequisites before enabling.
    </>
  );

  return (
    <Stack
      rowGap={16}
      columnGap={24}
      data-testid="rollout-behavior-field"
      direction={layout === 'horizontal' ? 'row' : 'column'}
    >
      <RolloutBehaviourOption
        name="restartPodsOneByOne"
        testId="restart-pods-one-by-one-field"
        checkboxTestId="restart-pods-one-by-one-checkbox"
        title={evictPodsOneByOne.title}
        description={evictPodsOneByOne.description}
        isDisabled={isRestartPodsOneByOneCannotBeUsed}
        size={size}
        LabelComponent={LabelComponent}
      />
      <DelaySecondsField size={size} LabelComponent={LabelComponent} />
      <Stack rowGap={8} data-testid="zero-downtime-field">
        <RolloutBehaviourOption
          name="zeroDowntime"
          checkboxTestId="zero-downtime-checkbox"
          title={zeroDowntime.title}
          description={zeroDowntime.description}
          isDisabled={isZeroDowntimeCannotBeUsed}
          size={size}
          LabelComponent={LabelComponent}
        />
        <SettingsInfoCallout>{zeroDowntimeInfo}</SettingsInfoCallout>
      </Stack>
    </Stack>
  );
};
